import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {PayslipState, FetchPayslipsResponse, FetchPayslipByIdResponse} from './types'
import {
  fetchPayslipById,
  fetchPayslips,
  createPayslip,
  updatePayslip,
  finalizePayslip,
  markPayslipPaid,
  deletePayslip,
  fetchDeletedPayslips,
  restorePayslip,
} from './service'

const initialState: PayslipState = {
  payslipForEdit: null,
  payslips: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
  deletedPayslips: [],
  deletedTotal: 0,
  deletedTotalPages: 0,
  deletedListLoading: false,
}

const payslipSlice = createSlice({
  name: 'payslip',
  initialState,
  reducers: {
    resetPayslips: (state: PayslipState) => {
      state.payslips = []
      state.total = 0
      state.totalPages = 0
    },
    setPayslipForEdit: (state: PayslipState, action: PayloadAction<any>) => {
      state.payslipForEdit = action.payload
    },
    clearPayslipForEdit: (state: PayslipState) => {
      state.payslipForEdit = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPayslips.pending, (state: PayslipState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchPayslips.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.listLoading = false
    })
    builder.addCase(
      fetchPayslips.fulfilled,
      (state: PayslipState, action: PayloadAction<FetchPayslipsResponse>) => {
        const {payslips, total, totalPages} = action.payload
        state.payslips = payslips
        state.total = total
        state.totalPages = totalPages
        state.listLoading = false
      }
    )

    builder.addCase(fetchPayslipById.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(fetchPayslipById.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      fetchPayslipById.fulfilled,
      (state: PayslipState, action: PayloadAction<FetchPayslipByIdResponse>) => {
        const {payslip} = action.payload
        state.payslipForEdit = payslip
        state.actionLoading = false
      }
    )

    builder.addCase(createPayslip.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createPayslip.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(createPayslip.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      state.payslips = [payslip, ...state.payslips]
      state.total = state.total + 1
      state.actionLoading = false
    })

    builder.addCase(updatePayslip.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updatePayslip.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(updatePayslip.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      state.payslips = state.payslips.map((p) => (p._id === payslip._id ? payslip : p))
      if (state.payslipForEdit?._id === payslip._id) {
        state.payslipForEdit = payslip
      }
      state.actionLoading = false
    })

    builder.addCase(finalizePayslip.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(finalizePayslip.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(finalizePayslip.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      state.payslips = state.payslips.map((p) => (p._id === payslip._id ? payslip : p))
      if (state.payslipForEdit?._id === payslip._id) {
        state.payslipForEdit = payslip
      }
      state.actionLoading = false
    })

    builder.addCase(markPayslipPaid.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(markPayslipPaid.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(markPayslipPaid.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      state.payslips = state.payslips.map((p) => (p._id === payslip._id ? payslip : p))
      if (state.payslipForEdit?._id === payslip._id) {
        state.payslipForEdit = payslip
      }
      state.actionLoading = false
    })

    builder.addCase(deletePayslip.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deletePayslip.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(deletePayslip.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      state.payslips = state.payslips.filter((p) => p._id !== payslip._id)
      state.total = state.total - 1
      if (state.payslipForEdit?._id === payslip._id) {
        state.payslipForEdit = null
      }
      state.actionLoading = false
    })

    builder.addCase(fetchDeletedPayslips.pending, (state: PayslipState) => {
      state.deletedListLoading = true
      state.error = null
    })
    builder.addCase(fetchDeletedPayslips.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.deletedListLoading = false
    })
    builder.addCase(
      fetchDeletedPayslips.fulfilled,
      (state: PayslipState, action: PayloadAction<any>) => {
        const {payslips, total, totalPages} = action.payload
        state.deletedPayslips = payslips
        state.deletedTotal = total
        state.deletedTotalPages = totalPages
        state.deletedListLoading = false
      }
    )

    builder.addCase(restorePayslip.pending, (state: PayslipState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(restorePayslip.rejected, (state: PayslipState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(restorePayslip.fulfilled, (state: PayslipState, action: PayloadAction<any>) => {
      const {payslip} = action.payload
      // Remove from deleted list
      state.deletedPayslips = state.deletedPayslips.filter((p) => p._id !== payslip._id)
      state.deletedTotal = state.deletedTotal - 1
      // Add to active list
      state.payslips = [payslip, ...state.payslips]
      state.total = state.total + 1
      state.actionLoading = false
    })
  },
})

export const {resetPayslips, setPayslipForEdit, clearPayslipForEdit} = payslipSlice.actions
export default payslipSlice.reducer

